import { useState } from 'react';
import FileUpload from 'components/FileUpload';
import DownloadBox from 'components/DownloadBox';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import ToolSeoContent from 'components/ToolSeoContent';
import { toolAPI, mkUrl } from 'utils/api';

export default function SplitPDF() {
  const [file, setFile]     = useState(null);
  const [fromPage, setFrom] = useState('');
  const [toPage, setTo]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [dlUrl, setDlUrl]   = useState('');
  const [resultInfo, setResultInfo] = useState(null);

  const reset = () => { setFile(null); setFrom(''); setTo(''); setError(''); setDlUrl(''); setResultInfo(null); };

  const handle = async () => {
    if (!file) { setError('Please select a PDF file first.'); return; }
    const from = parseInt(fromPage);
    const to   = parseInt(toPage);
    if (!fromPage || isNaN(from) || from < 1) { setError('Enter a valid "From" page number (minimum: 1).'); return; }
    if (!toPage   || isNaN(to)   || to < 1)   { setError('Enter a valid "To" page number.'); return; }
    if (from > to) { setError(`"From" page (${from}) must be less than or equal to "To" page (${to}).`); return; }
    setLoading(true); setError(''); setDlUrl(''); setResultInfo(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('fromPage', from);
      fd.append('toPage', to);
      const res = await toolAPI.splitPDF(fd);
      const totalPgs     = res.headers['x-total-pages'];
      const extractedPgs = res.headers['x-extracted-pages'];
      if (totalPgs) setResultInfo({ totalPgs, extractedPgs, from, to });
      setDlUrl(mkUrl(res.data));
    } catch (err) {
      setError(err.response?.data?.error || 'Split failed. Check your page range and try again.');
    } finally { setLoading(false); }
  };

  const stats = resultInfo ? [
    { value: resultInfo.totalPgs,     label: 'Total Pages' },
    { value: resultInfo.extractedPgs, label: 'Extracted Pages' },
    { value: `${resultInfo.from}–${resultInfo.to}`, label: 'Page Range' },
  ] : null;

  return (
    <>
    <Seo title="Split PDF — Extract Pages Free" description="Split a PDF or extract specific pages into a new file. Free, no signup." path="/tools/pdf/split" />
    <div className="tool-pg">
      <div className="container" style={{ maxWidth:720 }}>
        <div className="tool-hdr">
          <div className="tool-hdr-row">
            <div className="tool-hdr-icon"><Icon name="scissors" size={24} /></div>
            <div><h1>Split PDF</h1><p>Extract a range of pages from your PDF into a new document.</p></div>
          </div>
        </div>
        {!dlUrl ? (
          <div className="tool-box">
            <FileUpload accept=".pdf,application/pdf" onFileChange={setFile} hint="PDF files only · Max 50MB" />
            {file && <div className="alert alert-info" style={{ marginTop:14 }}><Icon name="info" size={16} /><span>File: <strong>{file.name}</strong></span></div>}
            <div style={{ marginTop:20 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:700, color:'var(--slate-700)', marginBottom:10 }}>Select Page Range to Extract</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div className="form-group" style={{ marginBottom:0 }}>
                  <label>From Page</label>
                  <input type="number" className="form-control" placeholder="e.g.  1" value={fromPage} onChange={e => setFrom(e.target.value)} min="1" style={{ fontSize:18, fontWeight:700, textAlign:'center' }} />
                </div>
                <div className="form-group" style={{ marginBottom:0 }}>
                  <label>To Page</label>
                  <input type="number" className="form-control" placeholder="e.g.  5" value={toPage} onChange={e => setTo(e.target.value)} min="1" style={{ fontSize:18, fontWeight:700, textAlign:'center' }} />
                </div>
              </div>
              {fromPage && toPage && parseInt(fromPage) > 0 && parseInt(toPage) >= parseInt(fromPage) && (
                <div style={{ marginTop:12, padding:'10px 16px', background:'var(--blue-50)', borderRadius:'var(--r-sm)', border:'1px solid var(--blue-200)', fontSize:14, color:'var(--blue-dd)' }}>
                  Will extract <strong>{parseInt(toPage) - parseInt(fromPage) + 1} page{parseInt(toPage) - parseInt(fromPage) + 1 !== 1 ? 's' : ''}</strong> — pages {fromPage} to {toPage}
                </div>
              )}
            </div>
            {error && <div className="alert alert-error" style={{ marginTop:14 }}><Icon name="alertCircle" size={16} />{error}</div>}
            <button className="btn btn-primary btn-lg btn-full" style={{ marginTop:20 }} onClick={handle} disabled={loading || !file}>
              {loading
                ? <><span className="spinner sp-sm" />Splitting PDF...</>
                : <><Icon name="scissors" size={18} />Split PDF — Extract Pages {fromPage||'?'} to {toPage||'?'}</>
              }
            </button>
          </div>
        ) : (
          <DownloadBox url={dlUrl} filename={`pages-${resultInfo?.from||fromPage}-to-${resultInfo?.to||toPage}.pdf`} onReset={reset} stats={stats} />
        )}
        <div className="alert alert-info" style={{ marginTop:14 }}>
          <Icon name="info" size={16} />
          <span>Enter start page in "From Page" and end page in "To Page". Example: From 2, To 5 extracts pages 2, 3, 4, and 5. Page numbers start from 1.</span>
        </div>
        <ToolSeoContent tool="split-pdf" />
      </div>
    </div>
  </>
  );
}
